import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/CtaBand";
import { PillButton } from "@/components/PillButton";
import { Icon } from "@/lib/icons";
import { IMG } from "@/lib/images";
import { Reveal } from "@/components/Reveal";
import { CardsSkeleton, ErrorState } from "@/components/States";
import { useIndustries } from "@/hooks/useSiteData";

export default function IndustriesPage() {
  const { data, isLoading, isError, refetch } = useIndustries();
  const { hash } = useLocation();
  useEffect(() => {
    if (hash && data) { const el = document.getElementById(hash.slice(1)); if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }
  }, [hash, data]);

  return (
    <>
      <Seo title="Industries" description="Jonaren Technology serves retail & CPG, manufacturing, BFSI, hospitality, education, TMT and logistics with tailored digital solutions." path="/industries" />
      <PageHero eyebrow="Industries we serve" title="Domain expertise that shortens the path to value." lead="We combine reusable accelerators with deep process knowledge in the industries where we've delivered for over a decade." crumbs={[{ label: "Industries" }]} image={IMG.cityOffice} testId="industries-page-hero">
        {data && <div className="mt-8 flex flex-wrap gap-2">{data.map((i) => <Link key={i.id} to={`#${i.slug}`} className="tag-pill" data-testid={`industry-jump-${i.slug}`}>{i.name}</Link>)}</div>}
      </PageHero>
      <section className="section paper">
        <div className="container-x space-y-6">
          {isLoading && <CardsSkeleton count={4} />}
          {isError && <ErrorState onRetry={refetch} />}
          {data?.map((ind, i) => (
            <Reveal key={ind.id}>
              <article id={ind.slug} className="grid scroll-mt-28 overflow-hidden rounded-3xl bg-white md:grid-cols-12" data-testid={`industry-detail-${ind.slug}`}>
                <div className={`relative min-h-[240px] md:col-span-5 ${i % 2 ? "md:order-2" : ""}`}><img src={ind.image} alt={ind.name} className="absolute inset-0 h-full w-full object-cover" loading="lazy" /><span className="absolute left-5 top-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-foreground"><Icon name={ind.icon} className="h-5 w-5" /></span></div>
                <div className="p-8 md:col-span-7 md:p-12">
                  <span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="mt-3 font-heading text-3xl font-medium leading-tight">{ind.name}</h2>
                  <p className="mt-3 text-muted-foreground">{ind.description}</p>
                  <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">{ind.outcomes.map((o) => <li key={o} className="flex items-center gap-2 text-sm font-medium"><CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />{o}</li>)}</ul>
                  <div className="mt-8"><PillButton to={`/contact?interest=${encodeURIComponent(ind.name)}`} variant="dark" size="sm" testId={`industry-contact-${ind.slug}`}>Discuss {ind.name.split(" ")[0].replace(",", "")} solutions</PillButton></div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
      <div className="paper"><CtaBand title="Don't see your industry?" text="Our engineering practices are industry-agnostic. Tell us about your context and we'll map the right approach." /></div>
    </>
  );
}
